import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapSystemUsersResponseToDto } from '../dto/system-users.dto.js';
import { buildSystemUsersTableModel } from '../model/system-user-row.model.js';
import { normalizeSystemUserActionLogs } from '../model/system-user-action-log.model.js';
import { mapAclProfileRolesResponseToDto, mapAclRolesResponseToDto } from '../../system-acl/dto/system-acl.dto.js';
import { getAssignedRoleIds } from '../../system-acl/model/system-acl.model.js';

function findSystemUserRow(payload, targetId) {
  const dto = mapSystemUsersResponseToDto(payload);
  const rows = buildSystemUsersTableModel(dto);
  return rows.find((row) => row.id === targetId) || null;
}

export function useSystemUserDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { token, hasPermission } = useAuth();
  const profileId = String(id || '').trim();

  const [activeTab, setActiveTab] = useState('info-acl');
  const [userRow, setUserRow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [isRolesSaving, setIsRolesSaving] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [actionLogs, setActionLogs] = useState([]);
  const [isActionLogsLoading, setIsActionLogsLoading] = useState(false);
  const [actionLogsError, setActionLogsError] = useState('');

  const canManageAcl = hasPermission('system.manage');
  const canViewActionLog = hasPermission('audit_log.view');
  const loadedProfileId = userRow?.id || '';
  const tabs = useMemo(() => {
    const nextTabs = [{ key: 'info-acl', label: 'systemUsers.infoAclTab' }];
    if (canViewActionLog) {
      nextTabs.push({ key: 'action-log', label: 'systemUsers.actionLogTitle' });
    }
    return nextTabs;
  }, [canViewActionLog]);

  const loadUser = useCallback(async () => {
    if (!profileId) {
      setUserRow(null);
      setError('User id is required.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getSystemUsers(token);
      const nextRow = findSystemUserRow(response, profileId);
      setUserRow(nextRow);
      if (!nextRow) {
        setError('User not found.');
      }
    } catch (requestError) {
      setUserRow(null);
      setError(requestError?.message || 'Failed to load user');
    } finally {
      setIsLoading(false);
    }
  }, [profileId, token]);

  const loadRoles = useCallback(async () => {
    if (!canManageAcl || !loadedProfileId) {
      setAssignedRoles([]);
      setSelectedRoleIds([]);
      return;
    }

    setIsRolesLoading(true);
    setRolesError('');

    try {
      const [rolesResponse, profileRolesResponse] = await Promise.all([
        availableRoles.length === 0 ? adminApi.getAclRoles(token) : Promise.resolve(null),
        adminApi.getProfileRoles(token, loadedProfileId),
      ]);

      if (rolesResponse) {
        setAvailableRoles(mapAclRolesResponseToDto(rolesResponse));
      }

      const nextAssignedRoles = mapAclProfileRolesResponseToDto(profileRolesResponse);
      setAssignedRoles(nextAssignedRoles);
      setSelectedRoleIds(getAssignedRoleIds(nextAssignedRoles));
    } catch (requestError) {
      setAssignedRoles([]);
      setSelectedRoleIds([]);
      setRolesError(requestError?.message || 'Failed to load assigned roles');
    } finally {
      setIsRolesLoading(false);
    }
  }, [availableRoles.length, canManageAcl, loadedProfileId, token]);

  const loadActionLogs = useCallback(async () => {
    if (!canViewActionLog || !loadedProfileId) {
      setActionLogs([]);
      return;
    }

    setIsActionLogsLoading(true);
    setActionLogsError('');

    try {
      const response = await adminApi.searchActionLogs(token, {
        actorUid: loadedProfileId,
        limit: 100,
        offset: 0,
        order: 'desc',
      });
      setActionLogs(normalizeSystemUserActionLogs(response));
    } catch (requestError) {
      setActionLogs([]);
      setActionLogsError(requestError?.message || 'Failed to load action log');
    } finally {
      setIsActionLogsLoading(false);
    }
  }, [canViewActionLog, loadedProfileId, token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (activeTab === 'action-log') {
      loadActionLogs();
    }
  }, [activeTab, loadActionLogs]);

  useEffect(() => {
    if (activeTab === 'action-log' && !canViewActionLog) {
      setActiveTab('info-acl');
    }
  }, [activeTab, canViewActionLog]);

  const toggleAssignedRole = useCallback((roleId) => {
    const normalizedId = Number(roleId || 0);
    if (!normalizedId) {
      return;
    }

    setSelectedRoleIds((current) => (
      current.includes(normalizedId)
        ? current.filter((item) => item !== normalizedId)
        : [...current, normalizedId].sort((left, right) => left - right)
    ));
  }, []);

  const saveAssignedRoles = useCallback(async () => {
    if (!loadedProfileId || !canManageAcl) {
      return;
    }

    setIsRolesSaving(true);
    setRolesError('');
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.updateProfileRoles(token, loadedProfileId, selectedRoleIds);
      const nextAssignedRoles = mapAclProfileRolesResponseToDto(response);
      setAssignedRoles(nextAssignedRoles);
      setSelectedRoleIds(getAssignedRoleIds(nextAssignedRoles));
      setSuccess('Roles updated.');
    } catch (requestError) {
      setRolesError(requestError?.message || 'Failed to update roles');
    } finally {
      setIsRolesSaving(false);
    }
  }, [canManageAcl, loadedProfileId, selectedRoleIds, token]);

  const goBack = useCallback(() => {
    navigate('/system/users');
  }, [navigate]);

  const changeActiveTab = useCallback((tab) => {
    if (tab === 'action-log' && !canViewActionLog) {
      return;
    }
    setActiveTab(tab === 'action-log' ? 'action-log' : 'info-acl');
  }, [canViewActionLog]);

  return {
    profileId,
    activeTab,
    tabs,
    userRow,
    isLoading,
    error,
    success,
    canManageAcl,
    canViewActionLog,
    availableRoles,
    assignedRoles,
    selectedRoleIds,
    isRolesLoading,
    isRolesSaving,
    rolesError,
    actionLogs,
    isActionLogsLoading,
    actionLogsError,
    setActiveTab: changeActiveTab,
    goBack,
    toggleAssignedRole,
    saveAssignedRoles,
  };
}
